import { Button, Divider, Group, Stack, Text, TextInput } from "@mantine/core";
import { useField } from "@mantine/form";
import { modals } from "@mantine/modals";
import { useOptInUserFormContext } from "shared/components/opt-in/context";
import { useFormState } from "shared/hooks/useFormState";
import { useOptInUserMutation } from "shared/hooks/useGraphql";
import { fail, runAsync } from "shared/utils/fns";
import { graphql } from "src/gql";
import { Cm } from "src/gql/graphql";
import { match } from "ts-pattern";

export const VerifyEudPanel = (props: { eudId: string; type: Cm; onConflict: () => void }) => {
    const form = useOptInUserFormContext();
    const [userId] = useFormState(form, "id");

    const code = useField({ initialValue: "", validateOnChange: true, validate: (it) => (it.trim().length == 0 ? "Required" : null) });

    const verifyEud = useOptInUserMutation({
        document: graphql(`
            mutation OptInVerifyEUD($userId: UUID!, $eudId: UUID!, $code: String!) {
                eud: verifyEUD(userId: $userId, eudId: $eudId, verificationCode: $code) {
                    id
                    isVerified
                }
            }
        `),
        throwOnError: false,
        onError() {
            props.onConflict?.();
        },
    });

    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                runAsync(async () => {
                    if (!!code.error) return;
                    if (!userId) return fail("User ID not found");

                    const { eud } = await verifyEud.mutateAsync({
                        userId,
                        eudId: props.eudId,
                        code: code.getValue(),
                    });

                    if (!eud.isVerified) {
                        code.setError("Invalid verification code");
                        return;
                    }

                    modals.closeAll();
                    form.setFieldValue("euds", (prev) =>
                        prev.map((each) => ({ ...each, isVerified: each.id == eud.id ? true : each.isVerified })),
                    );
                });
            }}
        >
            <Stack>
                <Text size="xs" c="dimmed">
                    We've sent a verification code to your{" "}
                    {match(props.type)
                        .with(Cm.Email, () => "email")
                        .with(Cm.Sms, () => "phone")
                        .otherwise(() => "device")}
                    .
                </Text>
                <TextInput placeholder="Verification code" {...code.getInputProps()} />
                <Group justify="flex-end">
                    <Divider mx="-md" />
                    <Button variant="default" onClick={() => modals.closeAll()}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={code.getValue().trim().length == 0} loading={verifyEud.isPending}>
                        Verify
                    </Button>
                </Group>
            </Stack>
        </form>
    );
};
